import { KeyboardEvent, MouseEvent, useMemo } from 'react';
import { Button, Col, Form, Stack } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faCheckCircle, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import moment from 'moment-timezone';

import CustomInputGroupWithIcon from 'src/components/custom_components/customInputGroupWithIcon';

import {
  getLanguage,
  getLocale,
  I18NextNS,
  LocaleCode,
} from 'src/models/Language.model';

import { DOB_INPUT_FORMATS } from 'src/utils/formatDate';
import {
  firstNameValidators,
  lastNameValidators,
} from 'src/utils/formValidators';
import {
  pidPattern,
  validateDatePattern,
  validateNameLength,
  validateNamePattern,
  validatePID,
  validatePIDLength,
} from 'src/utils/validationRules';

export interface SearchPatientFormValues {
  dob: string;
  lastName: string;
  firstName: string;
  id: string;
}

export type SearchPatientInfoFormGroupProps = {
  onSearchValuesChange: (values: SearchPatientFormValues) => void;
  onFormReset?: () => void;
  values?: SearchPatientFormValues;
};

export default function SearchPatientInfoFormGroup({
  onSearchValuesChange,
  onFormReset,
  values,
}: SearchPatientInfoFormGroupProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const methods = useForm<SearchPatientFormValues>({
    values: {
      dob: values?.dob || '',
      lastName: values?.lastName || '',
      firstName: values?.firstName || '',
      id: values?.id || '',
    },
  });

  const {
    register,
    control,
    setError,
    clearErrors,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = methods;

  const ValidFieldIcon = (field: keyof SearchPatientFormValues) => {
    return (
      getValues(field) &&
      !errors[field] && (
        <FontAwesomeIcon
          icon={faCheckCircle}
          className="text-success ms-2"
          data-test="field-validated"
        />
      )
    );
  };

  const registerDOB = {
    ...register('dob', {
      required: t('patientFields.dobRequired'),
    }),
    ref: undefined,
  };

  const handleSearchValuesChange = useMemo(
    () =>
      debounce(() => {
        if (Object.keys(errors).length === 0) {
          onSearchValuesChange(getValues());
        }
      }, 1000),
    [getValues, onSearchValuesChange, errors],
  );

  const hasAnyValue = () => {
    const values = getValues();
    return Boolean(
      values.firstName || values.lastName || values.id || values.dob,
    );
  };

  const handleClear = () => {
    reset();
    onFormReset?.();
  };

  return (
    <FormProvider {...methods}>
      <Stack
        direction="horizontal"
        gap={2}
        className="align-items-start flex-fill"
      >
        <Form.Group as={Col} controlId="dob">
          <Form.Label
            className="d-flex align-items-center"
            data-test="label-dob"
          >
            {t('patientFields.DOB')} ({t('dateFormat')}) {ValidFieldIcon('dob')}
          </Form.Label>

          <Controller
            control={control}
            {...registerDOB}
            render={({ field: { onChange, value } }) => (
              <div className="position-relative z-3">
                <CustomInputGroupWithIcon>
                  <DatePicker
                    locale={getLocale(getLanguage(language as LocaleCode))}
                    id="dob"
                    showIcon={false}
                    autoComplete="off"
                    wrapperClassName="flex-grow-1"
                    popperClassName="custom-datepicker-popper"
                    calendarClassName="custom-datepicker"
                    className="form-control rounded-start-0"
                    placeholderText={t('dateFormat')}
                    dateFormat={DOB_INPUT_FORMATS}
                    maxDate={new Date()}
                    showMonthDropdown
                    showYearDropdown
                    yearDropdownItemNumber={150}
                    scrollableYearDropdown
                    onChange={(
                      date: Date | null,
                      e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
                    ) => {
                      const target = e?.target as HTMLInputElement;
                      if (!target.value || validateDatePattern(target.value)) {
                        setValue('dob', moment(value).toDate().toDateString(), {
                          shouldDirty: true,
                        });
                        onChange(date);
                        handleSearchValuesChange();
                      }
                    }}
                    selected={value ? moment(value).toDate() : undefined}
                  />
                </CustomInputGroupWithIcon>
              </div>
            )}
          />
          {errors.dob?.message && (
            <span className="text-danger" data-test="filter-dob-error">
              {errors.dob?.message}
            </span>
          )}
        </Form.Group>

        <Form.Group as={Col} controlId="firstName">
          <Form.Label data-test="label-first-name">
            {t('patientFields.First Name')}
            {ValidFieldIcon('firstName')}
          </Form.Label>
          <CustomInputGroupWithIcon>
            <Form.Control
              placeholder={t('patientFields.firstNamePlaceholder')}
              {...register('firstName', firstNameValidators(t))}
              onChange={(e) => {
                if (e.target.value) {
                  if (!validateNamePattern(e.target.value)) {
                    setError('firstName', {
                      type: 'pattern',
                      message: t('patientFields.firstNameInvalid'),
                    });
                  } else if (!validateNameLength(e.target.value)) {
                    setError('firstName', {
                      type: 'maxLength',
                      message: t('patientFields.firstNameMaxLength'),
                    });
                  } else {
                    if (errors.firstName) {
                      clearErrors('firstName');
                    }
                  }
                } else {
                  clearErrors('firstName');
                }

                setValue('firstName', e.target.value, {
                  shouldDirty: true,
                });
                handleSearchValuesChange();
              }}
              autoComplete="off"
              data-test="filter-first-name"
            />
          </CustomInputGroupWithIcon>

          {errors.firstName?.message && (
            <span className="text-danger" data-test="filter-first-name-error">
              {errors.firstName?.message}
            </span>
          )}
        </Form.Group>

        <Form.Group as={Col} controlId="lastName">
          <Form.Label data-test="label-last-name">
            {t('patientFields.Last Name')} {ValidFieldIcon('lastName')}
          </Form.Label>
          <CustomInputGroupWithIcon>
            <Form.Control
              placeholder={t('patientFields.lastNamePlaceholder')}
              {...register('lastName', lastNameValidators(t))}
              onChange={(e) => {
                if (e.target.value) {
                  if (!validateNamePattern(e.target.value)) {
                    setError('lastName', {
                      type: 'pattern',
                      message: t('patientFields.lastNameInvalid'),
                    });
                  } else if (!validateNameLength(e.target.value)) {
                    setError('lastName', {
                      type: 'maxLength',
                      message: t('patientFields.lastNameMaxLength'),
                    });
                  } else {
                    if (errors.lastName) {
                      clearErrors('lastName');
                    }
                  }
                } else {
                  clearErrors('lastName');
                }

                setValue('lastName', e.target.value, { shouldDirty: true });
                handleSearchValuesChange();
              }}
              autoComplete="off"
              data-test="filter-last-name"
            />
          </CustomInputGroupWithIcon>

          {errors.lastName?.message && (
            <span className="text-danger" data-test="filter-last-name-error">
              {errors.lastName?.message}
            </span>
          )}
        </Form.Group>

        <Form.Group as={Col} controlId="id">
          <Form.Label data-test="label-patient-id">
            {t('patientFields.PID')}
            {ValidFieldIcon('id')}
          </Form.Label>

          <CustomInputGroupWithIcon>
            <Form.Control
              type="text"
              placeholder={t('patientFields.pidPlaceholder')}
              autoComplete="off"
              {...register('id', {
                required: t('patientFields.pidRequired'),
                maxLength: {
                  value: 20,
                  message: t('patientFields.pidMaxLength'),
                },
                pattern: {
                  value: pidPattern,
                  message: t('patientFields.pidInvalid'),
                },
              })}
              onChange={(e) => {
                if (e.target.value) {
                  if (!validatePIDLength(e.target.value)) {
                    setError('id', {
                      type: 'maxLength',
                      message: t('patientFields.pidMaxLength'),
                    });
                  } else if (!validatePID(e.target.value)) {
                    setError('id', {
                      type: 'pattern',
                      message: t('patientFields.pidInvalid'),
                    });
                  } else {
                    if (errors.id) {
                      clearErrors('id');
                    }
                  }
                } else {
                  clearErrors('id');
                }

                setValue('id', e.target.value, { shouldDirty: true });
                handleSearchValuesChange();
              }}
              data-test="filter-patient-id"
            />
          </CustomInputGroupWithIcon>

          {errors.id?.message && (
            <span className="text-danger" data-test="filter-patient-id-error">
              {errors.id?.message}
            </span>
          )}
        </Form.Group>

        <Button
          variant="outline-warning"
          className="d-flex align-self-end"
          onClick={handleClear}
          disabled={!hasAnyValue()}
          data-test="search-clear"
          data-track="exam.search-patient.clear"
          data-observe
        >
          <FontAwesomeIcon icon={faClose} />
        </Button>
      </Stack>
    </FormProvider>
  );
}
